import {
	CliNumberValidationOutcome,
	CliVerificationAttemptStatus,
	CliVerificationTriggerOutcome,
} from '../models/cli-verification.model';
import { I18nSipSetupEnum } from '../models/i18n.model';

/**
 * What the customer is shown after a trigger answer or a final attempt status. Several backend
 * values share a screen; each family has its own copy and its own next step.
 */
export type VerificationFamily =
	| 'running'
	| 'verified'
	| 'wrongCode'
	| 'notReached'
	| 'ourSide'
	| 'locked'
	| 'rateLimited'
	| 'inFlight'
	| 'cancelled'
	| 'notPossible'
	| 'declarationMissing';

/**
 * Unknown values land on 'ourSide' - a retry with the 032 fallback - and never on 'verified':
 * claiming the own number is signalled when it is not is the one wrong answer this screen must
 * not give.
 */
export function familyOfTrigger(outcome: CliVerificationTriggerOutcome): VerificationFamily {
	switch (outcome) {
		case 'TRIGGERED':
			return 'running';
		case 'ALREADY_VERIFIED':
			return 'verified';
		case 'NOT_ELIGIBLE_NO_SIP_TRUNK':
		case 'NUMBER_INELIGIBLE':
		case 'UNAVAILABLE':
		case 'IMPERSONATED_SESSION':
			return 'notPossible';
		case 'DECLARATION_MISSING':
			return 'declarationMissing';
		case 'LOCKED':
			return 'locked';
		case 'ATTEMPT_IN_FLIGHT':
			return 'inFlight';
		case 'RATE_LIMITED_COOLING_OFF':
		case 'RATE_LIMITED_NUMBER':
		case 'RATE_LIMITED_ACCOUNT':
		case 'RATE_LIMITED_GLOBAL':
			return 'rateLimited';
		case 'CALL_NOT_PLACED':
		default:
			return 'ourSide';
	}
}

export function familyOfStatus(status: CliVerificationAttemptStatus): VerificationFamily {
	switch (status) {
		case 'IN_FLIGHT':
		case 'ANSWERED':
			return 'running';
		case 'VERIFIED':
			return 'verified';
		case 'WRONG_CODE_EXHAUSTED':
			return 'wrongCode';
		case 'LOCKED_OUT':
			return 'locked';
		case 'NOT_ANSWERED':
		case 'BUSY':
		case 'REJECTED':
		case 'VOICEMAIL':
		case 'HUNG_UP':
			return 'notReached';
		case 'CANCELLED':
			return 'cancelled';
		case 'FAILED_TO_CONNECT':
		case 'TECHNICAL_FAILURE':
		case 'TIMED_OUT':
		default:
			return 'ourSide';
	}
}

export interface NumberStepVerdict {
	pass: boolean;
	/** The customer already proved this number; the verification call is skipped. */
	alreadyVerified: boolean;
	error: I18nSipSetupEnum | null;
}

/**
 * The number step's reading of `number/validate`. The backend checks the number, then the SIP
 * trunk, then ownership, so for a customer who is about to book the trunk NOT_ELIGIBLE_NO_SIP_TRUNK
 * means "the number itself is fine" - the order that follows is what fixes the rest.
 */
export function numberStepVerdict(outcome: CliNumberValidationOutcome): NumberStepVerdict {
	switch (outcome) {
		case 'OK':
		case 'NOT_ELIGIBLE_NO_SIP_TRUNK':
			return { pass: true, alreadyVerified: false, error: null };
		case 'ALREADY_VERIFIED_FOR_CUSTOMER':
			return { pass: true, alreadyVerified: true, error: null };
		case 'NOT_GERMAN':
			return reject(I18nSipSetupEnum.SIP_VALIDATE_NOT_GERMAN);
		case 'MOBILE':
			return reject(I18nSipSetupEnum.SIP_VALIDATE_MOBILE);
		case 'PREMIUM_OR_SERVICE':
			return reject(I18nSipSetupEnum.SIP_VALIDATE_PREMIUM);
		case 'NOMADIC':
			return reject(I18nSipSetupEnum.SIP_VALIDATE_NOMADIC);
		case 'NOT_FIXED_LINE':
			return reject(I18nSipSetupEnum.SIP_VALIDATE_NOT_FIXED_LINE);
		case 'INCOMPLETE':
			return reject(I18nSipSetupEnum.SIP_VALIDATE_INCOMPLETE);
		case 'UNAVAILABLE':
			return reject(I18nSipSetupEnum.SIP_VALIDATE_UNAVAILABLE);
		default:
			// An outcome this page has no copy for: let the trigger, which re-validates, decide.
			return { pass: true, alreadyVerified: false, error: null };
	}
}

function reject(error: I18nSipSetupEnum): NumberStepVerdict {
	return { pass: false, alreadyVerified: false, error };
}
